import React, {Suspense, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import {useSearchParams} from 'react-router-dom';
import clsx from 'clsx';
import {diApiNa} from '@di-shared/api/di';
import type {ApiResponseTripTagListResponse, TripTagInfo} from '@di-shared/api/di/__generated_na';
import {getYYYYMMddFormat} from '@di-shared/api/di/utils';
import {emitEventLog} from '@di-shared/features/analytics';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import useApiError from '@di-shared/features/common/useApiError';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import environmentStore from '@di-shared/features/environment/environmentStore';
import {HistoryDownloadFormProps} from '@di-shared/features/history/download/HistoryDownloadForm/index';
import {
  getHistoryFilterFormState,
  getHistoryFilterPeriodState,
  HISTORY_FILTER_PERIOD_NA,
  type HistoryFilterFormStateNa,
  parseHistoryFilterSearchParams,
  PERIOD_NA,
} from '@di-shared/features/history/filter/historyFilterQuery.na';
import {ROUTE_PATHS} from '@di-shared/routes';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useDashedDatetimeFormatter from '@di-shared/utils/useDashedDatetimeFormatter';
import {DistanceUnitType} from '@shared/cci/features/const/environment';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {addDate, endDateOfMonth, isSameYearAndMonth, startDateOfMonth, subDate} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';
import {useSuspenseQuery} from '@tanstack/react-query';

interface HistoryFilterTagsSectionProps {
  selectedTagNames: Array<string>;
  onChangeTagNames(tagNames: Array<string>): void;
}

interface HistoryFilterDriverSectionProps {
  vin: string;
  selectedDriverPuids: Array<string>;
  onChangeDriverPuids(puids: Array<string>): void;
}

export default function ({vin}: HistoryDownloadFormProps) {
  return (
    <ErrorBoundary fallback={<FallbackError />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner vin={vin} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin}: HistoryDownloadFormProps) {
  const {t} = useInferableTranslation();
  const {resolveError} = useApiError();
  const today = new Date();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const DashedDatetimeFormatter = useDashedDatetimeFormatter();
  const initialFilterForm = getHistoryFilterFormState(parseHistoryFilterSearchParams(searchParams));
  const [form, setForm] = useState<HistoryFilterFormStateNa>(initialFilterForm);

  const [selectedTagNames, setSelectedTagNames] = useState<Array<string>>(initialFilterForm.tagNames);
  const [selectedDriverPuids, setSelectedDriverPuids] = useState<Array<string>>([]);

  function resetForm() {
    const defaultForm = getHistoryFilterFormState(parseHistoryFilterSearchParams(new URLSearchParams()));
    setForm(defaultForm);
    setSelectedTagNames(defaultForm.tagNames);
    setSelectedDriverPuids([]);
  }

  async function openSelectMonthBottomSheet() {
    const months = Array.from({length: PERIOD_NA.SELECT_MONTH_COUNT}).map((_, index) => subDate(today, {months: index}));
    const date = await ccWebviewUtils.openBottomSheetWebViewUrl<Date>(ROUTE_PATHS.BOTTOM_SHEET_HISTORY_SELECT_MONTH.getFullPath(), {
      months,
      selectedDate: form.period === HISTORY_FILTER_PERIOD_NA.SELECT_MONTH ? (form.startDate ?? undefined) : undefined,
    });

    if (!date) {
      return;
    }

    setForm((prev) => ({
      tagNames: prev.tagNames,
      period: HISTORY_FILTER_PERIOD_NA.SELECT_MONTH,
      startDate: startDateOfMonth(date),
      endDate: isSameYearAndMonth(date, today) ? today : endDateOfMonth(date),
    }));
  }

  async function openStartDatePicker() {
    const date = await ccWebviewUtils.openBottomSheetWebViewUrl<Date | null>(
      ROUTE_PATHS.BOTTOM_SHEET_HISTORY_DOWNLOAD_DATEPICKER.getFullPath(),
      {
        selectedDate: form.startDate,
        minDate: form.endDate
          ? subDate(form.endDate, {days: PERIOD_NA.RECENT_90_DAYS - 1})
          : subDate(today, {months: PERIOD_NA.SELECT_MONTH_COUNT - 1}),
        maxDate: form.endDate ? form.endDate : today,
      }
    );

    if (!date) {
      return;
    }

    setForm((prev) => ({
      tagNames: prev.tagNames,
      period: prev.period,
      startDate: date,
      endDate: prev.endDate,
    }));
  }

  async function openEndDatePicker() {
    const date = await ccWebviewUtils.openBottomSheetWebViewUrl<Date | null>(
      ROUTE_PATHS.BOTTOM_SHEET_HISTORY_DOWNLOAD_DATEPICKER.getFullPath(),
      {
        selectedDate: form.endDate,
        minDate: form.startDate ? form.startDate : subDate(today, {months: PERIOD_NA.SELECT_MONTH_COUNT - 1}),
        maxDate:
          form.startDate && form.startDate < subDate(today, {days: PERIOD_NA.RECENT_90_DAYS - 1})
            ? addDate(form.startDate, {days: PERIOD_NA.RECENT_90_DAYS - 1})
            : today,
      }
    );

    if (!date) {
      return;
    }

    setForm((prev) => ({
      tagNames: prev.tagNames,
      period: prev.period,
      startDate: prev.startDate,
      endDate: date,
    }));
  }

  const [downloadExcel] = usePendingHandler(
    async () => {
      if (!form.startDate || !form.endDate) {
        return;
      }

      WebviewUtils.loading(true);
      const {distanceUnit} = environmentStore.getState();

      const downloadRequestParams: Parameters<typeof diApiNa.driveHistoryExcel.driveHistoryExcelDownloadToken>[0] = {
        vin,
        startDate: getYYYYMMddFormat(form.startDate),
        endDate: getYYYYMMddFormat(form.endDate),
        distanceUnit: distanceUnit === DistanceUnitType.KM ? 'KM' : 'MI',
        tagNames: selectedTagNames.length > 0 ? selectedTagNames : undefined,
        driverUserIds: selectedDriverPuids.length > 0 ? selectedDriverPuids : undefined,
      };

      const {data} = await diApiNa.driveHistoryExcel.driveHistoryExcelDownloadToken(downloadRequestParams);

      WebviewUtils.downloadUrl({
        fileName:
          `driving_${DashedDatetimeFormatter.format(form.startDate, 'date')}_${DashedDatetimeFormatter.format(form.endDate, 'date')}.xlsx`.replaceAll(
            '-',
            ''
          ),
        url: `${import.meta.env.VITE_DI_NA_API_URL}/drive-history/excel/file?token=${data}`,
        isOpenShare: true,
        mimeType: 'application/vnd.ms-excel',
        messages: {
          complete: t('msg_mytrips_download_toast_completed'),
          failure: t('msg_mytrips_download_toast_try'),
        },
      });

      WebviewUtils.loading(false);
      navigate(ROUTE_PATHS.HISTORY.getFullPath(vin));
    },
    async (error) => {
      WebviewUtils.loading(false);
      await resolveError(error);
      WebviewUtils.showToast(t('msg_mytrips_download_toast_try'));
      navigate(ROUTE_PATHS.HISTORY.getFullPath(vin));
    }
  );

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">
          <span className="blind">{t('ctn_mytrips_download')}</span>
        </h1>
      </Header>
      <main className="content page_my_trips">
        <section className="section">
          <div className="section_heading">
            <h2 className="section_heading_title">{t('ctn_mytrips_download')}</h2>
            <p className="section_heading_desc">{t('msg_mytrips_download_select_period')}</p>
          </div>
          <div className="setting_box">
            <h3 className="title_xxsmall">{t('msg_mytrips_download_select_month')}</h3>
            <button
              className="btn_select"
              type="button"
              onClick={openSelectMonthBottomSheet}
            >
              {form.period === HISTORY_FILTER_PERIOD_NA.SELECT_MONTH ? (
                <span className="select_value">{CldrDatetimeFormatter.format(form.startDate ?? today, 'yMMM')}</span>
              ) : (
                <span className="select_placeholder">{t('ctn_mytrips_select_month')}</span>
              )}
              <span
                className="ic ic_arrow_down_16px"
                aria-hidden="true"
              />
            </button>
          </div>
          <div className="setting_box">
            <h3 className="title_xxsmall">{t('msg_mytrips_download_select_period')}</h3>
            <div className="area_period">
              <ul className="list_period">
                <li className="list_period_item">
                  <button
                    className={clsx('btn btn_filled_tonal', {'is-selected': form.period === HISTORY_FILTER_PERIOD_NA.RECENT_7_DAYS})}
                    type="button"
                    onClick={() => setForm((prev) => ({...prev, ...getHistoryFilterPeriodState(HISTORY_FILTER_PERIOD_NA.RECENT_7_DAYS)}))}
                  >
                    {PERIOD_NA.RECENT_7_DAYS}&nbsp;
                    {t('ctn_mytrips_download_day_02')}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className={clsx('btn btn_filled_tonal', {'is-selected': form.period === HISTORY_FILTER_PERIOD_NA.RECENT_30_DAYS})}
                    type="button"
                    onClick={() => setForm((prev) => ({...prev, ...getHistoryFilterPeriodState(HISTORY_FILTER_PERIOD_NA.RECENT_30_DAYS)}))}
                  >
                    {PERIOD_NA.RECENT_30_DAYS}&nbsp;
                    {t('ctn_mytrips_download_day_02')}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className={clsx('btn btn_filled_tonal', {'is-selected': form.period === HISTORY_FILTER_PERIOD_NA.CUSTOM_DAYS})}
                    type="button"
                    onClick={() => setForm((prev) => ({...prev, ...getHistoryFilterPeriodState(HISTORY_FILTER_PERIOD_NA.CUSTOM_DAYS)}))}
                  >
                    {t('ctn_mytrips_filter_select_period_custom_eu')}
                  </button>
                </li>
              </ul>
              <div className="select_date_box">
                <div className="btn_box">
                  <button
                    className={clsx('btn btn_full btn_outline btn_date', {
                      type_placeholder: form.period !== HISTORY_FILTER_PERIOD_NA.CUSTOM_DAYS,
                    })}
                    type="button"
                    disabled={form.period !== HISTORY_FILTER_PERIOD_NA.CUSTOM_DAYS}
                    onClick={() => openStartDatePicker()}
                  >
                    <span className="body_large_r text_date">
                      {form.startDate ? CldrDatetimeFormatter.format(form.startDate, 'medium') : t('ctn_mytrips_download_startdate')}
                    </span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                  <span
                    className="body_large_b tilde"
                    role="none"
                  >
                    -
                  </span>
                  <button
                    className={clsx('btn btn_full btn_outline btn_date', {
                      type_placeholder: form.period !== HISTORY_FILTER_PERIOD_NA.CUSTOM_DAYS,
                    })}
                    type="button"
                    disabled={form.period !== HISTORY_FILTER_PERIOD_NA.CUSTOM_DAYS}
                    onClick={() => openEndDatePicker()}
                  >
                    <span className="body_large_r text_date">
                      {form.endDate ? CldrDatetimeFormatter.format(form.endDate, 'medium') : t('ctn_mytrips_download_enddate')}
                    </span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
        <HistoryFilterTagsSection
          selectedTagNames={selectedTagNames}
          onChangeTagNames={(tagNames) => setSelectedTagNames(tagNames)}
        />
        <HistoryFilterDriverSection
          vin={vin}
          selectedDriverPuids={selectedDriverPuids}
          onChangeDriverPuids={(puids) => setSelectedDriverPuids(puids)}
        />
      </main>
      <div className="floating_action_area">
        <div className="button_box_complex">
          <button
            type="button"
            className="btn btn_large btn_outline btn_full"
            onClick={resetForm}
          >
            {t('ctn_mytrips_filter_reset_eu')}
          </button>
          <button
            type="button"
            className="btn btn_large btn_filled btn_full"
            disabled={!form.startDate || !form.endDate}
            onClick={() => {
              emitEventLog({
                event: 'di_driveinfo_interaction',
                data_action: 'download driveinfo',
                data_name: t('ctn_mytrips_download_apply'),
              });
              downloadExcel();
            }}
          >
            {t('ctn_mytrips_download_apply')}
          </button>
        </div>
      </div>
    </div>
  );
}

function HistoryFilterTagsSection(props: HistoryFilterTagsSectionProps) {
  return (
    <section className="section">
      <ErrorBoundary fallback={<HistoryFilterTagsSectionErrorFallback />}>
        <Suspense fallback={<HistoryFilterTagsSectionLoading />}>
          <HistoryFilterTagsSectionInner {...props} />
        </Suspense>
      </ErrorBoundary>
    </section>
  );
}

function HistoryFilterTagsSectionInner({selectedTagNames, onChangeTagNames}: HistoryFilterTagsSectionProps) {
  const {t} = useInferableTranslation();

  const {data: tripTags} = useSuspenseQuery<ApiResponseTripTagListResponse, Error, Array<TripTagInfo>>({
    queryKey: [getQueryKey(diApiNa.tripTags.getAllTags)],
    queryFn: () => diApiNa.tripTags.getAllTags(),
    select: ({data}) => data.tags,
  });

  const isAllTagsSelected = selectedTagNames.length === 0;

  function toggleTagSelection(tagName: string) {
    if (selectedTagNames.length === 0) {
      onChangeTagNames([tagName]);
      return;
    }

    if (selectedTagNames.includes(tagName)) {
      const nextTagNames = selectedTagNames.filter((name) => name !== tagName);

      onChangeTagNames(nextTagNames.length > 0 ? nextTagNames : []);
      return;
    }

    onChangeTagNames([...selectedTagNames, tagName]);
  }

  return (
    <>
      <HistoryFilterTagsSectionHeader />
      <div className="tag_area">
        <button
          type="button"
          className={clsx('btn btn_round', {'is-selected': isAllTagsSelected})}
          onClick={() => onChangeTagNames([])}
        >
          <span
            className="ic ic_check_16px"
            aria-hidden="true"
          ></span>
          {t('ctn_mytrips_download_tags_all_eu')}
        </button>
        {tripTags.map(({tagName}) => (
          <button
            key={tagName}
            type="button"
            className={clsx('btn btn_round', {'is-selected': selectedTagNames.includes(tagName)})}
            onClick={() => toggleTagSelection(tagName)}
          >
            <span
              className="ic ic_check_16px"
              aria-hidden="true"
            ></span>
            {tagName}
          </button>
        ))}
      </div>
    </>
  );
}

function HistoryFilterTagsSectionHeader() {
  const {t} = useInferableTranslation();

  return (
    <div className="section_container_header">
      <h3 className="title_xxsmall">{t('ctn_mytrips_tags_create_title_eu')}</h3>
    </div>
  );
}

function HistoryFilterTagsSectionLoading() {
  const {t} = useInferableTranslation();

  return (
    <>
      <HistoryFilterTagsSectionHeader />
      <div className="tag_area">
        {Array.from({length: 4}).map((_, index) => (
          <span
            key={`history-filter-tag-skeleton-${index}`}
            className="btn btn_round skeleton"
            style={{width: 72, height: 32}}
            aria-hidden="true"
          />
        ))}
      </div>
      <span className="blind">{t('ctn_mytrips_tags_create_title_eu')}</span>
    </>
  );
}

function HistoryFilterTagsSectionErrorFallback() {
  const {t} = useInferableTranslation();

  return (
    <>
      <HistoryFilterTagsSectionHeader />
      <div className="trip_info">
        <div className="empty_area">
          <span className="ic ic_caution_32px"></span>
          <span
            className="body_small_r"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('msg_mytrips_tags_failed_to_load_eu') + '\n' + t('msg_mytrips_details_try_again_eu')}
          </span>
        </div>
      </div>
    </>
  );
}

function HistoryFilterDriverSection(props: HistoryFilterDriverSectionProps) {
  return (
    <section className="section">
      <ErrorBoundary fallback={<HistoryFilterDriverSectionErrorFallback />}>
        <Suspense fallback={<HistoryFilterDriverSectionLoading />}>
          <HistoryFilterDriverSectionInner {...props} />
        </Suspense>
      </ErrorBoundary>
    </section>
  );
}

function HistoryFilterDriverSectionInner({vin, selectedDriverPuids, onChangeDriverPuids}: HistoryFilterDriverSectionProps) {
  const {data: drivers} = useSuspenseQuery({
    queryKey: [getQueryKey(diApiNa.vehicle.retrieveDriverUser1), vin],
    queryFn: () => diApiNa.vehicle.retrieveDriverUser1({vin}),
    select: ({data}) => data,
  });

  const isAllDriversSelected = selectedDriverPuids.length === 0;

  function toggleDriverSelection(puid: string) {
    if (selectedDriverPuids.length === 0) {
      onChangeDriverPuids([puid]);
      return;
    }

    if (selectedDriverPuids.includes(puid)) {
      const nextPuids = selectedDriverPuids.filter((selectedPuid) => selectedPuid !== puid);

      onChangeDriverPuids(nextPuids.length > 0 ? nextPuids : []);
      return;
    }

    onChangeDriverPuids([...selectedDriverPuids, puid]);
  }

  return (
    <>
      <HistoryFilterDriverSectionHeader />
      <div className="tag_area">
        <button
          type="button"
          className={clsx('btn btn_round', {'is-selected': isAllDriversSelected})}
          onClick={() => onChangeDriverPuids([])}
        >
          <span
            className="ic ic_check_16px"
            aria-hidden="true"
          ></span>
          All
        </button>
        {drivers.map(({userId, driverName}) => (
          <button
            key={userId}
            type="button"
            className={clsx('btn btn_round', {'is-selected': selectedDriverPuids.includes(userId)})}
            onClick={() => toggleDriverSelection(userId)}
          >
            <span
              className="ic ic_check_16px"
              aria-hidden="true"
            ></span>
            {driverName}
          </button>
        ))}
      </div>
    </>
  );
}

function HistoryFilterDriverSectionHeader() {
  const {t} = useInferableTranslation();
  return (
    <div className="section_container_header">
      <h3 className="title_xxsmall">{t('ctn_mytrips_download_driver_title')}</h3>
    </div>
  );
}

function HistoryFilterDriverSectionLoading() {
  return (
    <>
      <HistoryFilterDriverSectionHeader />
      <div className="tag_area">
        {Array.from({length: 3}).map((_, index) => (
          <span
            key={`history-filter-driver-skeleton-${index}`}
            className="btn btn_round skeleton"
            style={{width: 72, height: 32}}
            aria-hidden="true"
          />
        ))}
      </div>
    </>
  );
}

function HistoryFilterDriverSectionErrorFallback() {
  const {t} = useInferableTranslation();

  return (
    <>
      <HistoryFilterDriverSectionHeader />
      <div className="trip_info">
        <div className="empty_area">
          <span className="ic ic_caution_32px"></span>
          <span
            className="body_small_r"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {/* FIXME: Driver 전용 i18n 키 없음(신규 키 등록 전까지 하드코딩) */}
            {'Failed to load drivers.\n' + t('msg_mytrips_details_try_again_eu')}
          </span>
        </div>
      </div>
    </>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">
          <span className="blind">{t('ctn_mytrips_download')}</span>
        </h1>
      </Header>
      <main className="content page_my_trips">
        <section className="section">
          <div className="section_heading">
            <h2
              className="section_heading_title skeleton"
              style={{width: '180px'}}
            >
              {t('ctn_mytrips_download')}
            </h2>
            <p
              className="section_heading_desc skeleton"
              style={{width: '220px'}}
            >
              {t('msg_mytrips_download_select_period')}
            </p>
          </div>
          <div className="setting_box">
            <h3
              className="title_xxsmall skeleton"
              style={{width: '250px'}}
            >
              {t('msg_mytrips_download_select_month')}
            </h3>
            <div
              className="area_period skeleton"
              style={{width: '374px', height: '38px'}}
            />
          </div>
          <div className="setting_box">
            <h3
              className="title_xxsmall skeleton"
              style={{width: '250px'}}
            >
              {t('msg_mytrips_download_select_period')}
            </h3>
            <div
              className="area_period skeleton"
              style={{width: '374px', height: '84px'}}
            />
          </div>
        </section>
        <section className="section">
          <HistoryFilterTagsSectionLoading />
        </section>
        <section className="section">
          <HistoryFilterDriverSectionLoading />
        </section>
      </main>
      <div className="floating_action_area">
        <div className="button_box_complex">
          <button
            type="button"
            className="btn btn_large btn_outline btn_full skeleton"
          >
            {t('ctn_mytrips_filter_reset_eu')}
          </button>
          <button
            type="button"
            className="btn btn_large btn_filled btn_full skeleton"
          >
            {t('ctn_mytrips_download_apply')}
          </button>
        </div>
      </div>
    </div>
  );
}

function FallbackError() {
  const {t} = useInferableTranslation();

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">
          <span className="blind">{t('ctn_mytrips_download')}</span>
        </h1>
      </Header>
      <main className="content page_my_trips">
        <section className="section">
          <div className="section_heading">
            <h2 className="section_heading_title">{t('ctn_mytrips_download')}</h2>
            <p className="section_heading_desc">{t('msg_mytrips_download_select_period')}</p>
          </div>
          <div className="setting_box">
            <h3 className="title_xxsmall">{t('msg_mytrips_download_select_month')}</h3>
            <button
              className="btn_select"
              type="button"
              disabled
            >
              <span className="select_placeholder">{t('ctn_mytrips_select_month')}</span>
              <span
                className="ic ic_arrow_down_16px"
                aria-hidden="true"
              />
            </button>
          </div>
          <div className="setting_box">
            <h3 className="title_xxsmall">{t('msg_mytrips_download_select_period')}</h3>
            <div className="area_period">
              <ul className="list_period">
                <li className="list_period_item">
                  <button
                    className="btn btn_filled_tonal"
                    type="button"
                    disabled
                  >
                    {PERIOD_NA.RECENT_7_DAYS}&nbsp;
                    {t('ctn_mytrips_download_day_02')}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className="btn btn_filled_tonal"
                    type="button"
                    disabled
                  >
                    {PERIOD_NA.RECENT_30_DAYS}&nbsp;
                    {t('ctn_mytrips_download_day_02')}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className="btn btn_filled_tonal"
                    type="button"
                    disabled
                  >
                    {t('ctn_mytrips_filter_select_period_custom_eu')}
                  </button>
                </li>
              </ul>
              <div className="select_date_box">
                <div className="btn_box">
                  <button
                    className="btn btn_full btn_outline btn_date type_placeholder"
                    type="button"
                    disabled
                  >
                    <span className="body_large_r text_date">{t('ctn_mytrips_download_startdate')}</span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                  <span
                    className="body_large_b tilde"
                    role="none"
                  >
                    -
                  </span>
                  <button
                    className="btn btn_full btn_outline btn_date type_placeholder"
                    type="button"
                    disabled
                  >
                    <span className="body_large_r text_date">{t('ctn_mytrips_download_enddate')}</span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="section">
          <HistoryFilterTagsSectionErrorFallback />
        </section>
        <section className="section">
          <HistoryFilterDriverSectionErrorFallback />
        </section>
      </main>
      <div className="floating_action_area">
        <div className="button_box_complex">
          <button
            type="button"
            className="btn btn_large btn_outline btn_full"
            disabled
          >
            {t('ctn_mytrips_filter_reset_eu')}
          </button>
          <button
            type="button"
            className="btn btn_large btn_filled btn_full"
            disabled
          >
            {t('ctn_mytrips_download_apply')}
          </button>
        </div>
      </div>
    </div>
  );
}
